import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Spec §11 — 12-step Group Onboarding wizard for organizers. Uses the 7 access
   presets (spec §7). Emits a group + trip skeleton + starter invitations. */

const STEPS = [
  { id: 1,  label: "Group name" },
  { id: 2,  label: "Trip title" },
  { id: 3,  label: "Destinations" },
  { id: 4,  label: "Dates" },
  { id: 5,  label: "Preparation template" },
  { id: 6,  label: "Invite members" },
  { id: 7,  label: "Assign roles" },
  { id: 8,  label: "Privacy preview" },
  { id: 9,  label: "Monitor progress" },
  { id: 10, label: "Announcements" },
  { id: 11, label: "Check-ins" },
  { id: 12, label: "Ready to launch" },
];

const PRESET_HINTS = {
  view:      { label: "Can View",       tone: "bg-blue-100 text-blue-700" },
  helper:    { label: "Helper",         tone: "bg-emerald-100 text-emerald-800" },
  organizer: { label: "Organizer",      tone: "bg-violet-100 text-violet-800" },
  traveler:  { label: "Traveling",      tone: "bg-amber-100 text-amber-800" },
  guardian:  { label: "Guardian",       tone: "bg-rose-100 text-rose-700" },
};

export default function GroupOnboarding() {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    group_name: "",
    trip_title: "",
    destinations: "",
    start_date: "",
    end_date: "",
    template_id: "international",
    invites: [{ email: "", preset: "traveler" }],
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setInvite = (i, k, v) => setForm((f) => ({
    ...f, invites: f.invites.map((inv, j) => j === i ? { ...inv, [k]: v } : inv),
  }));
  const addInvite = () => setForm((f) => ({ ...f, invites: [...f.invites, { email: "", preset: "traveler" }] }));

  const launch = async () => {
    setBusy(true);
    try {
      // 1) Create the trip
      const { data: trip } = await api.post("/trips", {
        title: form.trip_title || form.group_name,
        stops: form.destinations.split(",").map((s) => s.trim()).filter(Boolean),
        start_date: form.start_date, end_date: form.end_date,
        privacy: "selected",
      });
      // 2) Apply template
      if (form.template_id) {
        await api.post("/prep-templates/apply",
          { trip_id: trip.id, template_id: form.template_id }).catch(() => {});
      }
      // 3) Send invitations
      let sent = 0;
      for (const inv of form.invites) {
        if (!inv.email.includes("@")) continue;
        try {
          await api.post(`/trip-access/trips/${trip.id}/grants`,
            { email: inv.email, preset: inv.preset });
          sent += 1;
        } catch { /* skip failed */ }
      }
      toast.success(`Group launched! ${sent} invitation(s) sent.`);
      window.location.assign(`/trip/${trip.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Launch failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-8" data-testid="group-onboarding">
      <Link to="/travel-hub" className="inline-flex items-center gap-1 text-blue-700 text-sm hover:underline mb-3">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-amber-600 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Spec §11</div>
      <h1 className="font-serif font-800 text-4xl text-slate-900 mt-1">Group Onboarding</h1>

      {/* Progress rail */}
      <div className="mt-6 flex items-center gap-1 overflow-x-auto pb-2" data-testid="onboarding-progress">
        {STEPS.map((s) => (
          <div key={s.id} className={`shrink-0 flex-1 min-w-[24px] h-1.5 rounded-full ${s.id <= step ? "bg-blue-500" : "bg-slate-200"}`} />
        ))}
      </div>
      <div className="text-xs text-slate-500 mt-1">Step {step} of 12 — {STEPS[step - 1].label}</div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 space-y-4 min-h-[240px]">
        {step === 1 && <Fld label="What's the group name?" value={form.group_name} onChange={(v) => set("group_name", v)} placeholder="Smith Family Reunion" />}
        {step === 2 && <Fld label="Give the trip a title" value={form.trip_title} onChange={(v) => set("trip_title", v)} placeholder="Summer 2026 in Portugal" />}
        {step === 3 && <Fld label="Destinations (comma-separated)" value={form.destinations} onChange={(v) => set("destinations", v)} placeholder="Lisbon, Porto, Douro Valley" />}
        {step === 4 && (
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Start date" type="date" value={form.start_date} onChange={(v) => set("start_date", v)} />
            <Fld label="End date" type="date" value={form.end_date} onChange={(v) => set("end_date", v)} />
          </div>
        )}
        {step === 5 && (
          <div>
            <div className="text-sm text-slate-600 mb-3">Pick a preparation template — you can change or add more later.</div>
            <select value={form.template_id} onChange={(e) => set("template_id", e.target.value)}
              data-testid="onboarding-template"
              className="w-full rounded-xl border border-slate-200 py-2.5 px-3 outline-none focus:border-blue-500">
              <option value="international">International Trip</option>
              <option value="family">Family Vacation</option>
              <option value="pilgrimage">Pilgrimage</option>
              <option value="cruise">Cruise</option>
              <option value="school_alumni">School / Alumni</option>
              <option value="wedding">Destination Wedding</option>
              <option value="group_tour">Private Group Tour</option>
            </select>
          </div>
        )}
        {(step === 6 || step === 7) && (
          <div className="space-y-2.5">
            <div className="text-sm text-slate-600">Add people. {step === 7 && "Set their role — Traveling counts against your plan seats, all others are free."}</div>
            {form.invites.map((inv, i) => (
              <div key={i} className="flex gap-2">
                <input type="email" value={inv.email} onChange={(e) => setInvite(i, "email", e.target.value)}
                  data-testid={`onboarding-invite-email-${i}`}
                  placeholder="person@example.com"
                  className="flex-1 rounded-xl border border-slate-200 py-2 px-3 outline-none focus:border-blue-500 text-sm" />
                {step === 7 && (
                  <select value={inv.preset} onChange={(e) => setInvite(i, "preset", e.target.value)}
                    data-testid={`onboarding-invite-preset-${i}`}
                    className="rounded-xl border border-slate-200 py-2 px-3 outline-none focus:border-blue-500 text-sm">
                    <option value="view">Can View</option>
                    <option value="helper">Helper</option>
                    <option value="organizer">Organizer</option>
                    <option value="traveler">Traveling</option>
                    <option value="guardian">Guardian</option>
                  </select>
                )}
              </div>
            ))}
            <button type="button" onClick={addInvite}
              data-testid="onboarding-add-invite"
              className="text-sm font-700 text-blue-600 hover:underline">+ Add another</button>
          </div>
        )}
        {step === 8 && (
          <div className="space-y-2 text-sm text-slate-700" data-testid="onboarding-preview">
            <div className="font-800 text-slate-900">Privacy summary</div>
            <div>Group: <b>{form.group_name || "—"}</b></div>
            <div>Trip: <b>{form.trip_title || form.group_name || "—"}</b> ({form.destinations || "—"})</div>
            <div>Recipients:</div>
            <ul className="pl-4 space-y-1">
              {form.invites.filter((i) => i.email.includes("@")).map((i, x) => {
                const p = PRESET_HINTS[i.preset] || {};
                return <li key={x}>• {i.email} <span className={`ml-1 text-[10px] uppercase font-800 rounded-full px-1.5 py-0.5 ${p.tone || "bg-slate-100 text-slate-700"}`}>{p.label || i.preset}</span></li>;
              })}
            </ul>
            <div className="text-xs text-slate-500 mt-2">Confirmation numbers, costs, loyalty numbers, passports and documents are hidden by default on every recipient.</div>
          </div>
        )}
        {step === 9 && <Info body="After launch, you'll see who has completed which prep tasks — without seeing their passport or health info." />}
        {step === 10 && <Info body="You'll be able to send group announcements from the trip page. All authorized recipients get them via their chosen channels (Spec §14)." />}
        {step === 11 && <Info body="Turn on scheduled check-ins per day or activity. Missed check-ins alert Guardians (Spec §12)." />}
        {step === 12 && (
          <div className="text-center py-4" data-testid="onboarding-ready">
            <Sparkles className="w-12 h-12 text-blue-500 mx-auto mb-2" />
            <div className="font-serif font-800 text-2xl text-slate-900">Ready to launch</div>
            <div className="text-sm text-slate-600 mt-1">We'll create the trip, apply your template and send the invitations.</div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <button disabled={step === 1} onClick={() => setStep(step - 1)}
          className="text-sm font-700 text-slate-500 hover:text-slate-700 disabled:opacity-40 px-4 py-2">
          Back
        </button>
        {step < 12 ? (
          <button onClick={() => setStep(step + 1)} data-testid="onboarding-next"
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:brightness-110 text-white text-sm font-700 px-5 py-2">
            Next <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={launch} disabled={busy} data-testid="onboarding-launch"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:brightness-110 text-white text-sm font-700 px-5 py-2 disabled:opacity-60">
            <Check className="w-4 h-4" /> {busy ? "Launching…" : "Launch group trip"}
          </button>
        )}
      </div>
    </div>
  );
}

function Fld({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <div className="text-[13px] text-blue-600 font-600 mb-1">{label}</div>
      <input type={type} value={value || ""} placeholder={placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent text-slate-900 text-base py-1.5 border-0 border-b border-slate-300 focus:border-blue-500 focus:ring-0 outline-none placeholder:text-slate-300" />
    </label>
  );
}
function Info({ body }) { return <div className="text-sm text-slate-600 leading-relaxed">{body}</div>; }
